import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Plus, Trash2, ClipboardList, ArrowUp, ArrowDown, ArrowLeftRight, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin Store Paperwork Template tab. Lets admin curate the list of Debit
 * (Department) and Credit (Item) rows that regular users see on the daily
 * Z-Report. Admins still have add/delete on their own daily entry, so this
 * is purely the "default everyone-else gets" structure.
 */
export const TemplatesTab = () => {
  const [debit, setDebit] = useState([]);
  const [credit, setCredit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/store-paperwork-template`);
      setDebit(res.data?.debit_rows || []);
      setCredit(res.data?.credit_rows || []);
      setDirty(false);
    } catch {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip blanks; backend re-sanitizes anyway.
      const cleanD = debit.filter((r) => (r?.name || "").trim());
      const cleanC = credit.filter((r) => (r?.name || "").trim());
      const res = await axios.put(`${API}/admin/store-paperwork-template`, {
        debit_rows: cleanD,
        credit_rows: cleanC,
      });
      setDebit(res.data?.debit_rows || []);
      setCredit(res.data?.credit_rows || []);
      setDirty(false);
      toast.success("Template saved — all users will see this list on next page load");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  // One-click helper: scan both lists for any row whose name contains
  // "Paid Out" (case/space insensitive) and move it to the Credit side.
  // Paid-outs are always Credits (cash leaving the register offsets the
  // department sales tally), so if the admin template previously
  // classified them as Debit they can be fixed in bulk.
  const isPaidOutName = (name) => {
    const n = (name || "").toLowerCase().replace(/[\s-]+/g, " ").trim();
    return n.includes("paid out");
  };
  const movePaidOutToCredit = () => {
    const toMove = debit.filter((r) => isPaidOutName(r?.name));
    if (toMove.length === 0) {
      toast.info("No Paid-Out rows found on the Debit side — nothing to move.");
      return;
    }
    // Dedupe by name (case-insensitive) so we never push a duplicate into
    // Credit if one already exists there.
    const existingCreditNames = new Set(
      credit.map((r) => (r?.name || "").trim().toLowerCase())
    );
    const additions = toMove.filter(
      (r) => !existingCreditNames.has((r.name || "").trim().toLowerCase())
    );
    setCredit([...credit, ...additions]);
    setDebit(debit.filter((r) => !isPaidOutName(r?.name)));
    setDirty(true);
    toast.success(
      `Moved ${toMove.length} Paid-Out row${toMove.length === 1 ? "" : "s"} to Credit. Click Save Template to persist.`
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-templates-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-mono text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Store Paperwork Template
          </h2>
          <p className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
            Regular users see EXACTLY this list of rows on their daily Z-Report and can only enter dollar amounts —
            they cannot add or delete rows. You as an admin retain full add/delete on your own daily entry; that
            doesn&apos;t change this template. Tag a row name with{" "}
            <code className="px-1 rounded bg-emerald-700 text-amber-200">(T)</code> for state-tax base or{" "}
            <code className="px-1 rounded bg-emerald-700 text-amber-200">(Beer T)</code> for beer-tax base.
            Use the <ArrowLeftRight className="inline w-3 h-3 mx-0.5 -mt-0.5" /> button to move a row to the other
            side (Debit ↔ Credit) if it&apos;s on the wrong list.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={movePaidOutToCredit}
            disabled={loading || saving}
            variant="outline"
            className="btn-3d border-sky-500/60 text-sky-300 hover:text-sky-200"
            title="Move any row named '... Paid Out' from Debit → Credit"
            data-testid="template-fix-paidout-btn"
          >
            <Wand2 className="w-4 h-4 mr-1" />
            Move Paid-Out rows → Credit
          </Button>
          <Button
            onClick={save}
            disabled={loading || saving || !dirty}
            className="btn-3d"
            data-testid="template-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : dirty ? "Save Template" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RowListEditor
          title="Debit — Departments"
          rows={debit}
          setRows={(next) => { setDebit(next); setDirty(true); }}
          onMoveToOther={(i) => {
            const row = debit[i];
            if (!row) return;
            setDebit(debit.filter((_, idx) => idx !== i));
            setCredit([...credit, row]);
            setDirty(true);
            toast.success(`Moved "${row.name || 'row'}" to Credit`);
          }}
          moveToOtherLabel="Move to Credit"
          placeholder="Department name (suffix with (T) or (Beer T))"
          testIdPrefix="tpl-debit"
          accent="rose"
        />
        <RowListEditor
          title="Credit — Items (Network / EFT)"
          rows={credit}
          setRows={(next) => { setCredit(next); setDirty(true); }}
          onMoveToOther={(i) => {
            const row = credit[i];
            if (!row) return;
            setCredit(credit.filter((_, idx) => idx !== i));
            setDebit([...debit, row]);
            setDirty(true);
            toast.success(`Moved "${row.name || 'row'}" to Debit`);
          }}
          moveToOtherLabel="Move to Debit"
          placeholder="Item name"
          testIdPrefix="tpl-credit"
          accent="emerald"
        />
      </div>
    </div>
  );
};

function RowListEditor({ title, rows, setRows, placeholder, testIdPrefix, accent = "primary", onMoveToOther, moveToOtherLabel = "Move to other side" }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const accentBg = accent === "rose" ? "border-rose-500/40 bg-rose-500/5" : accent === "emerald" ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/50";

  const update = (i, name) => {
    const next = safeRows.slice();
    next[i] = { ...next[i], name };
    setRows(next);
  };
  const add = () => setRows([...safeRows, { name: "" }]);
  const remove = (i) => setRows(safeRows.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= safeRows.length) return;
    const next = safeRows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };

  return (
    <Card className={accentBg} data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="font-mono text-sm">{title}</CardTitle>
        <span className="font-mono text-xs text-muted-foreground">{safeRows.length} rows</span>
      </CardHeader>
      <CardContent className="space-y-1">
        {safeRows.length === 0 && (
          <p className="font-mono text-xs text-muted-foreground text-center py-4">
            No rows yet — click + Add Row to start building the template.
          </p>
        )}
        {safeRows.map((row, i) => (
          <div key={i} className="grid grid-cols-[24px,1fr,32px,32px,32px,32px] gap-1 items-center">
            <span className="font-mono text-[10px] text-muted-foreground text-right">{i + 1}.</span>
            <Input
              value={row.name || ""}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="h-7 text-xs font-mono"
              data-testid={`${testIdPrefix}-name-${i}`}
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="h-7 w-7"
              title="Move up"
              data-testid={`${testIdPrefix}-up-${i}`}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => move(i, 1)}
              disabled={i === safeRows.length - 1}
              className="h-7 w-7"
              title="Move down"
              data-testid={`${testIdPrefix}-down-${i}`}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onMoveToOther && onMoveToOther(i)}
              disabled={!onMoveToOther}
              className="h-7 w-7 text-sky-400 hover:text-sky-300"
              title={moveToOtherLabel}
              data-testid={`${testIdPrefix}-swap-${i}`}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => remove(i)}
              className="h-7 w-7 text-rose-400 hover:text-rose-300"
              title="Remove row"
              data-testid={`${testIdPrefix}-del-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <div className="pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            className="btn-3d h-7"
            data-testid={`${testIdPrefix}-add`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Row
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default TemplatesTab;
